import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Statistic, Button, Tag, Spin } from 'antd';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { UnitsApi } from 'api/api';
import {
  UnitBasicInfoDto,
  UnitStatisticsDto,
  UnitDealHistoryDto,
  UnitAttachmentDto,
  UnitCategoryFloorDto
} from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main } from 'container/styled';
import { hasPermission } from 'utility/accessibility/hasPermission';
import openNotificationWithIcon from 'utility/notification';
import { formatCurrency, formatDate, formatSquareMeters } from 'utility/utility';
import { UnitAttachments } from './UnitAttachments';
import { BuildingVisualization } from './BuildingVisualization';
import { UnitCreateModal } from './UnitCreateModal';

const unitsApi = new UnitsApi();

export const UnitDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Split state for different data sections
  const [unit, setUnit] = useState<UnitBasicInfoDto>();
  const [statistics, setStatistics] = useState<UnitStatisticsDto>();
  const [floors, setFloors] = useState<UnitCategoryFloorDto[]>([]);
  const [dealHistory, setDealHistory] = useState<UnitDealHistoryDto[]>([]);
  const [attachments, setAttachments] = useState<UnitAttachmentDto[]>([]);

  // Loading states for different sections
  const [basicLoading, setBasicLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [floorsLoading, setFloorsLoading] = useState(false);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  const [editModalVisible, setEditModalVisible] = useState(false);

  // Fetch basic unit info first (lightweight)
  const fetchBasicUnitInfo = async () => {
    if (!id) return;
    try {
      setBasicLoading(true);
      const response = await unitsApi.apiUnitsIdGet({ id: parseInt(id) });
      setUnit(response.data);
    } catch (error) {
      openNotificationWithIcon('error', t('units:fetch-error', 'Failed to fetch unit details'));
    } finally {
      setBasicLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    if (!id) return;
    try {
      setStatsLoading(true);
      const response = await unitsApi.apiUnitsIdStatisticsGet({ id: parseInt(id) });
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch floors data
  const fetchFloors = async () => {
    if (!id) return;
    try {
      setFloorsLoading(true);
      const response = await unitsApi.apiUnitsIdFloorsGet({ id: parseInt(id) });
      setFloors(response.data);
    } catch (error) {
      console.error('Failed to fetch floors:', error);
    } finally {
      setFloorsLoading(false);
    }
  };

  // Fetch deal history
  const fetchDealHistory = async () => {
    if (!id) return;
    try {
      setDealsLoading(true);
      const response = await unitsApi.apiUnitsIdDealsGet({ id: parseInt(id) });
      setDealHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch deal history:', error);
    } finally {
      setDealsLoading(false);
    }
  };

  // Fetch attachments
  const fetchAttachments = async () => {
    if (!id) return;
    try {
      setAttachmentsLoading(true);
      const response = await unitsApi.apiUnitsIdAttachmentsGet({ id: parseInt(id) });
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  // Load all data
  const fetchAllData = async () => {
    if (!id) return;

    // Load basic info first
    await fetchBasicUnitInfo();

    // Then load other data in parallel
    await Promise.all([
      fetchStatistics(),
      fetchFloors(),
      fetchDealHistory(),
      fetchAttachments()
    ]);
  };

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const handleUpdate = async () => {
    try {
      await fetchAllData();
      setEditModalVisible(false);
      openNotificationWithIcon('success', t('units:updated', 'Unit updated successfully'));
    } catch (error) {
      openNotificationWithIcon('error', t('units:update-error', 'Error updating unit'));
    }
  };

  const handleEdit = () => {
    setEditModalVisible(true);
  };

  // Statistics cards data
  const statisticsCards = [
    {
      title: t('units:total-deals', 'Total Deals'),
      value: statistics?.totalDeals || 0,
      icon: 'shopping-bag',
      color: '#1890ff',
    },
    {
      title: t('units:initial-stock', 'Initial Stock'),
      value: statistics?.initialStock || 0,
      icon: 'package',
      color: '#52c41a',
    },
    {
      title: t('units:current-stock', 'Current Stock'),
      value: statistics?.currentStock || 0,
      icon: 'box',
      color: '#faad14',
    },
    {
      title: t('units:free-square-meters', 'Available m²'),
      value: formatSquareMeters(statistics?.freeSquareMeters || 0),
      icon: 'square',
      color: '#13c2c2',
    },
    {
      title: t('units:reserved', 'Reserved'),
      value: statistics?.reserved || 0,
      icon: 'clock',
      color: '#722ed1',
    },
    {
      title: t('units:sold', 'Sold'),
      value: statistics?.sold || 0,
      icon: 'check-circle',
      color: '#f5222d',
    },
  ];

  const dealColumns = [
    {
      title: t('deals:client', 'Client'),
      dataIndex: 'clientName',
      key: 'clientName',
      render: (text: string, record: UnitDealHistoryDto) => <Link to={`/clients/${record.clientId}`}>{text}</Link>,
    },
    {
      title: t('deals:quantity', 'Quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('deals:price-per-sqm', 'Price per m²'),
      dataIndex: 'negotiatedPricePerSqm',
      key: 'negotiatedPricePerSqm',
      render: (value: number) => formatCurrency(value),
    },
    {
      title: t('deals:total-price', 'Total Price'),
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (value: number) => formatCurrency(value),
    },
    {
      title: t('deals:date', 'Date'),
      dataIndex: 'dealDate',
      key: 'dealDate',
      render: (value: string) => formatDate(value),
    },
    {
      title: t('deals:status', 'Status'),
      dataIndex: 'dealStatus',
      key: 'dealStatus',
      render: (status: string) => (
        <Tag color={status === 'Active' ? 'green' : 'red'}>
          {t(`projects:deal-status-${status.toLowerCase()}`, status)}
        </Tag>
      ),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'actions',
      render: (_: any, record: UnitDealHistoryDto) => (
        <Button
          type="link"
          icon={<FeatherIcon icon="eye" size={16} />}
          onClick={() => navigate(`/deals/${record.dealId}`)}
        ></Button>
      ),
    },
  ];

  // Deal History Card Component
  const DealHistoryCard: React.FC = () => (
    <Card title={t('units:deal-history', 'Deal History')} loading={dealsLoading}>
      <Table
        dataSource={dealHistory}
        columns={dealColumns}
        pagination={false}
        rowKey="dealId"
      />
    </Card>
  );

  // Statistics Cards Component
  const StatisticsCards: React.FC = () => (
    <Row gutter={[16, 16]}>
      {statisticsCards.map((stat, index) => (
        <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
          <Card bordered={false} style={{ textAlign: 'center' }} loading={statsLoading}>
            <FeatherIcon icon={stat.icon} size={24} style={{ marginBottom: 8, color: stat.color }} />
            <Statistic title={stat.title} value={stat.value} />
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Floors Card Component
  const FloorsCard: React.FC = () => (
    <Card title={t('units:floor-allocations-label', 'Floor Allocations')} loading={floorsLoading}>
      <BuildingVisualization floors={floors} onFloorClick={() => { }} />
    </Card>
  );

  // Attachments Card Component  
  const AttachmentsCard: React.FC = () => (
    <Card title={t('units:attachments', 'Attachments')} loading={attachmentsLoading}>
      <UnitAttachments
        unitId={unit?.id!}
        attachments={attachments}
        loading={attachmentsLoading}
        onRefresh={fetchAttachments}
      />
    </Card>
  );

  // Check if unit has floors (you might need to add this property to UnitBasicInfoDto)
  const hasFloors = floors && floors.length > 0;

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={unit?.name ?? ''}
          subTitle={
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', fontSize: '14px' }}>
              <Link to={`/unit-categories/${unit?.categoryId}`}>{unit?.categoryName}</Link>
              <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>•</span>
              <Link to={`/projects/${unit?.projectId}`}>{unit?.projectName}</Link>
              <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>•</span>
              <span>{formatSquareMeters(unit?.squareMeters || 0)}</span>
            </div>
          }
          buttons={[
            hasPermission('units:edit') && (
              <Button key="edit" type="primary" icon={<FeatherIcon icon="edit" size={16} />} onClick={handleEdit}>
                {t('global:edit', 'Edit')}
              </Button>
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        {/* Show basic loading spinner if basic info is still loading */}
        <Spin spinning={basicLoading}>
          {/* Statistics Cards */}
          <StatisticsCards />

          <Row gutter={[16, 16]} className="mt-4">
            {hasFloors ? (
              <>
                <Row gutter={[16, 16]} style={{ width: '100%', margin: 0 }}>
                  <Col span={12}>
                    <AttachmentsCard />
                  </Col>
                  <Col span={12}>
                    <FloorsCard />
                  </Col>
                </Row>

                <Row style={{ width: '100%', marginTop: 16 }}>
                  <Col span={24}>
                    <DealHistoryCard />
                  </Col>
                </Row>
              </>
            ) : (
              <Row gutter={[16, 16]} style={{ width: '100%', margin: 0 }}>
                <Col span={14}>
                  <DealHistoryCard />
                </Col>
                <Col span={10}>
                  <AttachmentsCard />
                </Col>
              </Row>
            )}
          </Row>
        </Spin>

        {/* Edit Modal */}
        {editModalVisible && unit && (
          <UnitCreateModal
            categoryHasFloors={hasFloors}
            visible={editModalVisible}
            categoryId={unit.categoryId!}
            floors={floors}
            unit={unit as any} // You might need to adapt this based on the modal's expected props
            onCancel={() => setEditModalVisible(false)}
            onSuccess={handleUpdate}
          />
        )}
      </Main>
    </>
  );
};